import React, { useEffect, useRef, useState } from 'react';
import styled from 'astroturf/react';

import { Body } from 'js/design_system/Body';

import { BundleWork } from './types';
import { WorkCard } from './WorkCard';

const ROTATE_INTERVAL_MS = 5000;

/**
 * V7 (`rotating_preview_v7`) replacement for `PaperPreviewList`.
 * Cycles through the bundle works on a 5-second interval — at any
 * given moment shows `paperLimit` papers, but over the modal's
 * lifetime cycles through more. Tests "show how much, through motion"
 * vs "show how much, through count."
 *
 * Pauses only while a descendant has focus (keyboard accessibility).
 * Earlier versions also paused on mouse-hover-of-container, but that
 * defeated the variant: the cursor is in the preview region for the
 * dominant population, so rotation almost never advanced — variant
 * users saw a static list, indistinguishable from control.
 *
 * Desktop only — the dispatch site (`AutotestBulkDownload.ts` ARMS)
 * falls back to `CONTROL.renderPreviewList` (PaperPreviewList) on
 * mobile.
 */
export interface RotatingPreviewProps {
  works: ReadonlyArray<BundleWork>;
  paperLimit: number;
  totalRelatedCount: number;
  includedWork?: BundleWork;
  showAnnotations?: boolean;
  showRelevancy?: boolean;
  /** Rendered in place of the cycling slice when `works` is empty AND no
   *  `includedWork` is provided. Mirrors `PaperPreviewList`'s shape so
   *  all renderers share the same empty-filter UX. */
  emptyState?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export function RotatingPreview({
  works,
  paperLimit,
  totalRelatedCount,
  includedWork,
  showAnnotations = false,
  showRelevancy = true,
  emptyState,
  className,
  style,
}: RotatingPreviewProps) {
  const [offset, setOffset] = useState(0);
  const pausedRef = useRef(false);

  useEffect(() => {
    if (works.length <= paperLimit) return;
    const id = window.setInterval(() => {
      if (pausedRef.current) return;
      setOffset((prev) => (prev + paperLimit) % works.length);
    }, ROTATE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [works.length, paperLimit]);

  const showEmptyState = works.length === 0 && !includedWork && emptyState;

  // Wrap-around slice so the rotation loops cleanly past the end.
  const slice: BundleWork[] = [];
  for (let i = 0; i < paperLimit && i < works.length; i++) {
    slice.push(works[(offset + i) % works.length]);
  }

  return (
    <Container
      className={className}
      style={style}
      onFocusCapture={() => {
        pausedRef.current = true;
      }}
      onBlurCapture={() => {
        pausedRef.current = false;
      }}
    >
      {showEmptyState ? (
        emptyState
      ) : (
        <>
          <Caption>
            <Body size="xs">
              Showing {Math.min(paperLimit, works.length)}–{totalRelatedCount} ·
              auto-rotating
            </Body>
          </Caption>
          <ScrollArea>
            {includedWork && (
              <WorkCard
                work={includedWork}
                showRelevancy={false}
                showAnnotations={showAnnotations}
              />
            )}
            {slice.map((work) => (
              <WorkCard
                key={`${offset}-${work.id}`}
                work={work}
                showRelevancy={showRelevancy}
                showAnnotations={showAnnotations}
              />
            ))}
          </ScrollArea>
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Caption = styled.div`
  padding: var(--spacing-xs-8, 8px) var(--spacing-xs-16, 16px);
  color: var(--text-secondary, #535366);
`;

const ScrollArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-right: 6px;
`;
